/**
 * Theme-editor embed nudge — the dashboard card that gets merchants to
 * switch on the `autonoma-ai-editor` app embed (Autonoma AI inside
 * Shopify's theme editor). Sits at the top of the suggestion area.
 *
 *   GET  /api/theme-editor-admin/nudge          → { show, state, deepLink, editorUrl }
 *   "Turn on AI editor" → lib/themeEditorTurnOn.turnOnAiEditor(): the SAME
 *        flow as Settings — inside the click, copy the connect code and
 *        open the theme editor in a NEW TAB (activateAppId while the embed
 *        is off), then POST /turn-on (ai_editor_enabled = true, records the
 *        nudge click). The card pre-fetches the new-tab URL and a code
 *        (GET /status, GET /pairing-code?mint=1) so the synchronous part
 *        has them. Falls back to opening the plain deep link in a new tab
 *        if that call fails.
 *   POST /api/theme-editor-admin/nudge/dismiss  "Not now" — hidden 14 days
 *   POST /api/theme-editor-admin/nudge/confirm-seen  the one-time success card was seen
 *
 * States: nudge → almost (clicked, embed still off) → confirm (embed
 * found on — shown once) → done / hidden (renders nothing). The bell entry
 * of type 'embed_nudge' shares this state on the backend.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Text } from '@shopify/polaris';
import { apiFetch } from '../lib/api';
import { TURN_ON_LABEL, openNewTab, turnOnAiEditor } from '../lib/themeEditorTurnOn';

const TITLE = "Edit your store with AI, right inside Shopify's theme editor";
const BODY_NUDGE = 'Turn on Autonoma AI, click any section, type what you want changed. Done.';
const BODY_ALMOST = 'Almost there. Open the theme editor and click Save to finish turning on Autonoma AI.';
const BODY_CONFIRM = '✓ Autonoma AI is on in your theme editor. Open the theme editor, click any section and start typing.';
const NOTE = 'Then click Save in the top right';

const post = (path) => apiFetch(path, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
const refreshBell = () => { try { window.dispatchEvent(new CustomEvent('autonoma:refresh-notifications')); } catch { /* test env */ } };

export default function EmbedNudgeCard() {
  const [nudge, setNudge] = useState(null); // GET /nudge payload, null = nothing to show
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/theme-editor-admin/nudge', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      setNudge(res.ok && data.show ? data : null);
    } catch { setNudge(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Pre-fetch what the click needs synchronously: the new-tab editor URL
  // (activateAppId while the embed is off) and a connect code to copy.
  const [pre, setPre] = useState(null);
  const showing = !!nudge && (nudge.state === 'nudge' || nudge.state === 'almost');
  useEffect(() => {
    if (!showing) return;
    let alive = true;
    (async () => {
      try {
        const [s, c] = await Promise.all([
          apiFetch('/api/theme-editor-admin/status', { credentials: 'include' }).then(r => r.json()).catch(() => ({})),
          apiFetch('/api/theme-editor-admin/pairing-code?mint=1', { credentials: 'include' }).then(r => r.json()).catch(() => ({})),
        ]);
        if (alive) setPre({ newTabUrl: s.newTabUrl || null, pairing: c.code ? { code: c.code } : (s.pairing || null) });
      } catch { /* the click still works — the tab opens after /turn-on */ }
    })();
    return () => { alive = false; };
  }, [showing]);

  if (!nudge) return null;

  const turnOn = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await turnOnAiEditor({ prefetched: pre }); // live theme; new tab + code copied inside the click
    } catch {
      // The switch could not be flipped (network, gate) — the plain
      // activation deep link still gets the merchant into the editor.
      try { await post('/api/theme-editor-admin/nudge/clicked'); } catch { /* the deep link still works */ }
      openNewTab(nudge.deepLink);
    } finally { setBusy(false); }
  };

  const notNow = async () => {
    setNudge(null); // hide immediately — the write is best-effort
    try { await post('/api/theme-editor-admin/nudge/dismiss'); } catch { /* re-asked next visit */ }
    refreshBell();
  };

  const confirmSeen = async () => {
    setNudge(null);
    try { await post('/api/theme-editor-admin/nudge/confirm-seen'); } catch { /* shown again next visit at worst */ }
    refreshBell();
  };

  if (nudge.state === 'confirm') {
    return (
      <div className="au-suggestion" data-testid="embed-nudge-card" data-state="confirm">
        <span className="au-suggestion__icon" aria-hidden="true">🎨</span>
        <div style={{ flex: 1 }}>
          <div className="au-suggestion__title">{BODY_CONFIRM}</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            {nudge.editorUrl && (
              <Button onClick={() => openNewTab(nudge.editorUrl)}>Open theme editor</Button>
            )}
            <Button variant="plain" onClick={confirmSeen}>Dismiss</Button>
          </div>
        </div>
      </div>
    );
  }

  if (nudge.state !== 'nudge' && nudge.state !== 'almost') return null;

  return (
    <div className="au-suggestion" data-testid="embed-nudge-card" data-state={nudge.state}>
      <span className="au-suggestion__icon" aria-hidden="true">🎨</span>
      <div style={{ flex: 1 }}>
        <div className="au-suggestion__title">{TITLE}</div>
        <div className="au-suggestion__msg">{nudge.state === 'almost' ? BODY_ALMOST : BODY_NUDGE}</div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="primary" onClick={turnOn} loading={busy} disabled={!nudge.deepLink} data-testid="embed-nudge-turn-on">
            {TURN_ON_LABEL}
          </Button>
          <Button variant="plain" onClick={notNow} data-testid="embed-nudge-not-now">Not now</Button>
        </div>
        <div style={{ marginTop: 8 }}>
          <Text as="p" tone="subdued" variant="bodySm">{NOTE}</Text>
        </div>
      </div>
    </div>
  );
}
